// Property Detail Page with Magic Rent Price
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Grid, Card, Box, Typography, Button, Chip } from "@mui/material";
import { ArrowBack, Edit, AttachMoney } from "@mui/icons-material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import MagicRentPrice from "components/MagicRentPrice";
import { propertyService } from "services/firestoreService";
import { useAuth } from "contexts/AuthContext";

function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPropertyDetails();
  }, [id]);

  const fetchPropertyDetails = async () => {
    try {
      const data = await propertyService.getById(id);
      setProperty(data);
    } catch (error) {
      console.error("Error fetching property:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox py={3} textAlign="center">
          <Typography>Loading property details...</Typography>
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  if (!property) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox py={3} textAlign="center">
          <Typography>Property not found</Typography>
          <Button onClick={() => navigate("/properties/all")} startIcon={<ArrowBack />}>
            Back to Properties
          </Button>
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        {/* Header */}
        <ArgonBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <ArgonTypography variant="h4" fontWeight="bold">
              {property.name || property.address}
            </ArgonTypography>
            <ArgonTypography variant="body2" color="text">
              {property.property_type} • {property.bedrooms} bed • {property.bathrooms} bath
            </ArgonTypography>
          </Box>
          <Box display="flex" gap={2}>
            <ArgonButton
              variant="outlined"
              color="primary"
              startIcon={<ArrowBack />}
              onClick={() => navigate("/properties/all")}
            >
              Back
            </ArgonButton>
            <ArgonButton
              variant="contained"
              color="primary"
              startIcon={<Edit />}
              onClick={() => navigate(`/properties/${id}`)}
            >
              Edit
            </ArgonButton>
          </Box>
        </ArgonBox>

        {/* Main Content Grid */}
        <Grid container spacing={3}>
          {/* Property Details Card */}
          <Grid item xs={12} md={8}>
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                  Property Information
                </ArgonTypography>
                
                <Grid container spacing={2}>
                  <Grid item xs={6} md={4}>
                    <ArgonTypography variant="caption" color="text">
                      Current Rent
                    </ArgonTypography>
                    <ArgonTypography variant="h5" fontWeight="bold">
                      ${property.monthly_rent || 0}/mo
                    </ArgonTypography>
                  </Grid>
                  
                  <Grid item xs={6} md={4}>
                    <ArgonTypography variant="caption" color="text">
                      Square Footage
                    </ArgonTypography>
                    <ArgonTypography variant="h5" fontWeight="bold">
                      {property.square_footage || "N/A"} sqft
                    </ArgonTypography>
                  </Grid>
                  
                  <Grid item xs={6} md={4}>
                    <ArgonTypography variant="caption" color="text">
                      Year Built
                    </ArgonTypography>
                    <ArgonTypography variant="h5" fontWeight="bold">
                      {property.year_built || "N/A"}
                    </ArgonTypography>
                  </Grid>
                  
                  <Grid item xs={12}>
                    <ArgonTypography variant="caption" color="text">
                      Address
                    </ArgonTypography>
                    <ArgonTypography variant="body1">
                      {property.address}, {property.city}, {property.state} {property.zip_code}
                    </ArgonTypography>
                  </Grid>
                </Grid>
              </ArgonBox>
            </Card>
          </Grid>

          {/* Magic Rent Price Card */}
          <Grid item xs={12} md={4}>
            <MagicRentPrice 
              propertyId={property.id} 
              currentRent={property.monthly_rent}
            />
          </Grid>

          {/* Additional Features */}
          <Grid item xs={12}>
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="h6" fontWeight="medium" mb={2}>
                  Quick Actions
                </ArgonTypography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <ArgonButton variant="outlined" fullWidth size="large">
                      Schedule Maintenance
                    </ArgonButton>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <ArgonButton variant="outlined" fullWidth size="large">
                      View Tenants
                    </ArgonButton>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <ArgonButton variant="outlined" fullWidth size="large">
                      Financial Report
                    </ArgonButton>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <ArgonButton variant="outlined" fullWidth size="large">
                      List for Rent
                    </ArgonButton>
                  </Grid>
                </Grid>
              </ArgonBox>
            </Card>
          </Grid>
        </Grid>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default PropertyDetail;